import axios, { AxiosError } from "axios";
import { AuthResponse, MeResponse, ApiError } from "./types";

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000/api",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to requests automatically
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle errors globally with logging
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    //error logging
    const errorLog = {
      timestamp: new Date().toISOString(),
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      errors: error.response?.data?.errors,
    };

    if (process.env.NODE_ENV === "development") {
      console.error("🚨 API Error:", errorLog);
    } else {
      console.error("API Error:", {
        url: errorLog.url,
        status: errorLog.status,
        message: errorLog.message,
      });
    }

    if (error.response?.status === 401) {
      console.warn("⚠️ Unauthorized - Redirecting to login");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

// Auth API functions
export const authApi = {
  register: async (
    name: string,
    email: string,
    password: string,
    password_confirmation: string
  ) => {
    const response = await api.post<AuthResponse>("/v1/register", {
      name,
      email,
      password,
      password_confirmation,
    });
    return response.data;
  },

  login: async (email: string, password: string) => {
    const response = await api.post<AuthResponse>("/v1/login", {
      email,
      password,
    });
    return response.data;
  },

  logout: async () => {
    await api.post("/v1/logout");
  },

  me: async () => {
    const response = await api.get<MeResponse>("/v1/me");
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post<{ message: string }>("/v1/forgot-password", {
      email,
    });
    return response.data;
  },

  resetPassword: async (
    email: string,
    password: string,
    password_confirmation: string,
    token: string
  ) => {
    const response = await api.post<{ message: string }>("/v1/reset-password", {
      email,
      password,
      password_confirmation,
      token,
    });
    return response.data;
  },

  /** Accepts a "Set your password" link (POST /v1/set-password). */
  setPassword: async (
    email: string,
    password: string,
    password_confirmation: string,
    token: string
  ) => {
    const response = await api.post<{ message: string }>("/v1/set-password", {
      email,
      password,
      password_confirmation,
      token,
    });
    return response.data;
  },

  verifyEmail: async (id: string, hash: string, expires: string, signature: string) => {
    const response = await api.get<{ message: string }>("/v1/verify-email", {
      params: { id, hash, expires, signature },
    });
    return response.data;
  },

  resendVerification: async (email: string) => {
    const response = await api.post<{ message: string }>("/v1/resend-verification", {
      email,
    });
    return response.data;
  },
};
